import { apiClient, type Paginated } from '@/core/api';
import type { LessonAssetKind } from '@/features/curriculum';
import type {
  AssignHomeworkInput,
  CreateLinkArtifactInput,
  GradeSubmissionInput,
  HomeworkAssetDto,
  HomeworkAssignmentDto,
  HomeworkDetailDto,
  HomeworkListDto,
  HomeworkListParams,
  HomeworkWriteInput,
  SubmissionArtifactDto,
  SubmissionArtifactKind,
  SubmissionDto,
  SubmissionListParams,
} from './types';

// ---------------------------------------------------------------------------
// Homework
// ---------------------------------------------------------------------------

/** `GET /api/homework/` — staff see all; pupils only published homework of
 *  their active classes. Parents get 403 (they read `/submissions/`). */
export async function getHomeworkList(
  params: HomeworkListParams = {}
): Promise<Paginated<HomeworkListDto>> {
  const { data } = await apiClient.get<Paginated<HomeworkListDto>>('/homework/', { params });
  return data;
}

/** `GET /api/homework/{public_id}/` — blocks arrive read-enriched. */
export async function getHomework(publicId: string): Promise<HomeworkDetailDto> {
  const { data } = await apiClient.get<HomeworkDetailDto>(`/homework/${publicId}/`);
  return data;
}

/** `POST /api/homework/` — any staff member; the caller becomes the creator
 *  (ADR-030). Classes are issued separately via `assignHomework`. */
export async function createHomework(input: HomeworkWriteInput): Promise<HomeworkDetailDto> {
  const { data } = await apiClient.post<HomeworkDetailDto>('/homework/', input);
  return data;
}

/** `PATCH /api/homework/{public_id}/` — save sends the FULL blocks array. */
export async function updateHomework(
  publicId: string,
  input: HomeworkWriteInput
): Promise<HomeworkDetailDto> {
  const { data } = await apiClient.patch<HomeworkDetailDto>(`/homework/${publicId}/`, input);
  return data;
}

/** `DELETE /api/homework/{public_id}/` — archives (C4); submissions stay. */
export async function archiveHomework(publicId: string): Promise<void> {
  await apiClient.delete(`/homework/${publicId}/`);
}

/** `POST /api/homework/{public_id}/publish/`. */
export async function publishHomework(publicId: string): Promise<HomeworkDetailDto> {
  const { data } = await apiClient.post<HomeworkDetailDto>(`/homework/${publicId}/publish/`);
  return data;
}

/** `POST /api/homework/{public_id}/unpublish/`. */
export async function unpublishHomework(publicId: string): Promise<HomeworkDetailDto> {
  const { data } = await apiClient.post<HomeworkDetailDto>(`/homework/${publicId}/unpublish/`);
  return data;
}

/** `POST /api/homework/{public_id}/duplicate/` — the reuse path (ADR-030):
 *  the copy is the caller's own unassigned draft. Issuing to more classes is
 *  `assignHomework`, not a copy. */
export async function duplicateHomework(publicId: string): Promise<HomeworkDetailDto> {
  const { data } = await apiClient.post<HomeworkDetailDto>(`/homework/${publicId}/duplicate/`, {});
  return data;
}

/** `POST /api/homework/{public_id}/assign/` — issue this homework to a class
 *  with ITS OWN due date (ADR-030). Admin or a teacher of that class, on
 *  homework the caller may write. */
export async function assignHomework(
  publicId: string,
  input: AssignHomeworkInput
): Promise<HomeworkAssignmentDto> {
  const { data } = await apiClient.post<HomeworkAssignmentDto>(
    `/homework/${publicId}/assign/`,
    input
  );
  return data;
}

/** `POST /api/homework/{public_id}/unassign/` — hide it from one class;
 *  submissions and grades stay (C4). */
export async function unassignHomework(publicId: string, schoolClass: string): Promise<void> {
  await apiClient.post(`/homework/${publicId}/unassign/`, { school_class: schoolClass });
}

/** `POST /api/homework-assets/` — staff multipart upload with progress; the
 *  HomeworkAsset mirror of `/lesson-assets/` (same kinds and limits). */
export async function uploadHomeworkAsset(
  kind: LessonAssetKind,
  file: File,
  onProgress?: (percent: number) => void
): Promise<HomeworkAssetDto> {
  const form = new FormData();
  form.append('kind', kind);
  form.append('file', file);
  const { data } = await apiClient.post<HomeworkAssetDto>('/homework-assets/', form, {
    onUploadProgress: event => {
      if (onProgress && event.total) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    },
  });
  return data;
}

// ---------------------------------------------------------------------------
// Submissions
// ---------------------------------------------------------------------------

/** `GET /api/submissions/` — pupils: own; parents: their children's (reduced
 *  payload); staff: all, with the review-queue filters. */
export async function getSubmissions(
  params: SubmissionListParams = {}
): Promise<Paginated<SubmissionDto>> {
  const { data } = await apiClient.get<Paginated<SubmissionDto>>('/submissions/', { params });
  return data;
}

/** `GET /api/submissions/{public_id}/`. */
export async function getSubmission(publicId: string): Promise<SubmissionDto> {
  const { data } = await apiClient.get<SubmissionDto>(`/submissions/${publicId}/`);
  return data;
}

/** `POST /api/submissions/` `{homework}` — the pupil's get-or-create of their
 *  own draft (201 when created, 200 when it already existed). */
export async function openSubmission(homeworkId: string): Promise<SubmissionDto> {
  const { data } = await apiClient.post<SubmissionDto>('/submissions/', { homework: homeworkId });
  return data;
}

/** `PATCH /api/submissions/{public_id}/` — the text answer; 400
 *  `submission_graded` once graded. */
export async function updateSubmissionText(publicId: string, text: string): Promise<SubmissionDto> {
  const { data } = await apiClient.patch<SubmissionDto>(`/submissions/${publicId}/`, { text });
  return data;
}

/** `POST /api/submissions/{public_id}/submit/` — hand in or resubmit. */
export async function submitSubmission(publicId: string): Promise<SubmissionDto> {
  const { data } = await apiClient.post<SubmissionDto>(`/submissions/${publicId}/submit/`);
  return data;
}

/** `POST /api/submissions/{public_id}/grade/` — score ≤ homework.max_score. */
export async function gradeSubmission(
  publicId: string,
  input: GradeSubmissionInput
): Promise<SubmissionDto> {
  const { data } = await apiClient.post<SubmissionDto>(`/submissions/${publicId}/grade/`, input);
  return data;
}

/** `POST /api/submissions/{public_id}/return/` — request a revision (feedback
 *  required); reopens the submission for the pupil. */
export async function returnSubmission(publicId: string, feedback: string): Promise<SubmissionDto> {
  const { data } = await apiClient.post<SubmissionDto>(`/submissions/${publicId}/return/`, {
    feedback,
  });
  return data;
}

// ---------------------------------------------------------------------------
// Submission artifacts (pupil-owned: an upload XOR a link)
// ---------------------------------------------------------------------------

/** `POST /api/submission-artifacts/` — multipart upload with progress. */
export async function uploadSubmissionArtifact(
  submissionId: string,
  kind: Exclude<SubmissionArtifactKind, 'link'>,
  file: File,
  onProgress?: (percent: number) => void
): Promise<SubmissionArtifactDto> {
  const form = new FormData();
  form.append('submission', submissionId);
  form.append('kind', kind);
  form.append('file', file);
  const { data } = await apiClient.post<SubmissionArtifactDto>('/submission-artifacts/', form, {
    onUploadProgress: event => {
      if (onProgress && event.total) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    },
  });
  return data;
}

/** `POST /api/submission-artifacts/` with `{kind: 'link', url}` — Loom,
 *  YouTube or any other http(s) link. */
export async function createLinkArtifact(
  input: CreateLinkArtifactInput
): Promise<SubmissionArtifactDto> {
  const { data } = await apiClient.post<SubmissionArtifactDto>('/submission-artifacts/', {
    submission: input.submission,
    kind: 'link',
    url: input.url,
  });
  return data;
}

/** `DELETE /api/submission-artifacts/{public_id}/` — the owner while not
 *  graded; staff anytime. */
export async function deleteSubmissionArtifact(publicId: string): Promise<void> {
  await apiClient.delete(`/submission-artifacts/${publicId}/`);
}
